import { Link } from 'react-router'
import {
  RiArrowRightLine,
  RiGlobalLine,
  RiShieldLine,
  RiHeartLine,
  RiLeafLine,
} from 'react-icons/ri'
import { ROUTES } from '@/constants/routes'
import { useInView } from '@/hooks'

const values = [
  {
    title: 'Culturally Aware',
    body: 'Her lived experience as a Black woman navigating pregnancy and motherhood means every client receives support that is deeply culturally informed.',
    Icon: RiGlobalLine,
  },
  {
    title: 'Trauma-Informed',
    body: 'Trained to hold space for difficult experiences with care and sensitivity, ensuring every woman feels safe to share and process her journey.',
    Icon: RiShieldLine,
  },
  {
    title: 'Non-Judgmental',
    body: 'A safe space where women and families feel truly seen, heard, and valued — every choice is met with empathy and unwavering respect.',
    Icon: RiHeartLine,
  },
  {
    title: 'Holistically Focused',
    body: 'Support that honours the whole person — physical, mental, and emotional — not just the clinical aspects of birth and postpartum.',
    Icon: RiLeafLine,
  },
]

export const AboutStorySection = () => {
  const { ref: quoteRef, inView: quoteInView } = useInView<HTMLDivElement>()
  const { ref: storyRef, inView: storyInView } = useInView<HTMLDivElement>()
  const { ref: valuesRef, inView: valuesInView } = useInView<HTMLDivElement>()
  const { ref: ctaRef, inView: ctaInView } = useInView<HTMLDivElement>()

  return (
    <>
      {/* ── Pull quote ── */}
      <section className="bg-white py-16 lg:py-20 border-b border-base-100">
        <div
          ref={quoteRef}
          className={`max-w-[900px] mx-auto px-6 lg:px-12 text-center space-y-5 ${quoteInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto" />
          <blockquote
            className="font-display italic text-base-800 leading-relaxed"
            style={{ fontSize: 'clamp(1.35rem, 3vw, 2rem)' }}
          >
            "At the core of Simone's work is a deep commitment to walking alongside women and
            families, holding space, honouring their experiences, and supporting them every step of
            the way."
          </blockquote>
          <div className="w-12 h-[3px] rounded-full bg-gradient-to-r from-gold-500 to-crimson-600 mx-auto" />
        </div>
      </section>

      {/* ── Story ── */}
      <section className="bg-base-50 py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div
            ref={storyRef}
            className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start"
          >
            {/* Left col */}
            <div className={`space-y-6 ${storyRef ? '' : ''}`}>
              <div className={storyInView ? 'animate-fade-in-up' : 'opacity-0'}>
                <h2
                  className="font-display font-bold text-base-900 leading-tight mb-4"
                  style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
                >
                  A Foundation Built on{' '}
                  <em className="text-crimson-600 not-italic italic">Advocacy</em>
                </h2>
                <div className="w-10 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500" />
              </div>

              <div
                className={`space-y-4 text-[15px] text-base-600 leading-[1.9] ${storyInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: storyInView ? '0.15s' : undefined }}
              >
                <p>
                  Simone is a passionate advocate and the founder of H.E.R Birth Keeper, bringing
                  over 15 years of experience within the Violence Against Women and Girls (VAWG)
                  sector. Her work has been rooted in championing the safety, dignity, and
                  empowerment of women, ensuring that even the most vulnerable voices are heard,
                  supported, and respected, particularly during times of crisis.
                </p>
                <p>
                  Known for her caring, calm, and patient nature, Simone creates a safe,
                  non-judgmental space where women and families feel truly seen, heard, and valued.
                  She understands that every journey is unique and meets each client with empathy,
                  respect, and unwavering support.
                </p>
              </div>

              {/* Stat */}
              <div
                className={`flex items-center gap-5 p-5 bg-white rounded-2xl border border-base-100 shadow-[0_4px_14px_-4px_rgba(155,28,28,0.08)] ${storyInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: storyInView ? '0.25s' : undefined }}
              >
                <span
                  className="font-display font-bold text-gold-500 leading-none shrink-0"
                  style={{ fontSize: '3.5rem' }}
                >
                  15+
                </span>
                <p className="text-[13.5px] text-base-600 leading-snug">
                  Years experience within the Violence Against Women and Girls (VAWG) sector
                </p>
              </div>
            </div>

            {/* Right col */}
            <div
              className={`space-y-6 ${storyInView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: storyInView ? '0.1s' : undefined }}
            >
              <div>
                <h2
                  className="font-display font-bold text-base-900 leading-tight mb-4"
                  style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
                >
                  Shaped by <em className="text-crimson-600 not-italic italic">Lived Experience</em>
                </h2>
                <div className="w-10 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500" />
              </div>

              <div className="space-y-4 text-[15px] text-base-600 leading-[1.9]">
                <p>
                  As a mother of two, Simone's personal journey as a Black woman navigating
                  pregnancy, birth, and motherhood has deeply shaped her approach to care. Her lived
                  experiences, combined with her professional background, allow her to provide
                  support that is not only compassionate and nurturing but also culturally aware and
                  trauma-informed.
                </p>
                <p>
                  Simone's approach is grounded in education, empowerment, and trust. She is
                  committed to ensuring that every woman she supports feels informed, confident, and
                  equipped to make decisions that are right for herself and her baby.
                </p>
                <p>
                  She believes that when women are supported holistically and given the knowledge
                  they need, they are better able to navigate their birth and parenting journey with
                  strength, clarity, and confidence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div ref={valuesRef} className="text-center mb-14">
            <h2
              className={`font-display font-bold text-base-900 leading-tight mb-4 ${valuesInView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                animationDelay: valuesInView ? '0.1s' : undefined,
              }}
            >
              Support That is <em className="text-crimson-600 not-italic italic">Different</em>
            </h2>
            <div
              className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto ${valuesInView ? 'animate-fade-in' : 'opacity-0'}`}
              style={{ animationDelay: valuesInView ? '0.2s' : undefined }}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={v.title}
                className={`bg-base-50 rounded-2xl p-6 border border-base-100 hover:border-crimson-200 hover:shadow-[0_8px_24px_-4px_rgba(155,28,28,0.1)] transition-all duration-300 ${valuesInView ? 'animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: valuesInView ? `${i * 0.08}s` : undefined }}
              >
                <span className="w-10 h-10 rounded-full bg-crimson-50 flex items-center justify-center mb-4">
                  <v.Icon size={18} className="text-crimson-600" />
                </span>
                <h3 className="font-display font-bold text-base-900 text-lg mb-2">{v.title}</h3>
                <p className="text-[13.5px] text-base-500 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section ref={ctaRef} className="bg-crimson-50 border-t border-crimson-100 py-16 lg:py-20">
        <div
          className={`max-w-[700px] mx-auto px-6 lg:px-12 text-center space-y-6 ${ctaInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <h2
            className="font-display font-bold text-base-900 leading-tight"
            style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
          >
            Ready to Feel <em className="text-crimson-600 not-italic italic">Walked Alongside?</em>
          </h2>
          <div className="w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto" />
          <p className="text-base-600 text-[15px] leading-[1.85]">
            Book a free support call with Simone to find out which service is right for you and
            begin your journey with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Link to={ROUTES.ENQUIRY} className="btn-primary">
              Book a Free Support Call
              <RiArrowRightLine size={17} />
            </Link>
            <Link to={ROUTES.SERVICES} className="btn-outline">
              View Services
              <RiArrowRightLine size={17} />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
